import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileChips, UploadProgress, dropProps, UserAvatar } from "@/components/shared";
import { MessageSquare, Send, Paperclip } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const MessageThread = ({ endpoint, myRole, title, emptyText, placeholder }) => {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api.get(endpoint).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const pickFiles = async (e) => {
    const files = e?.target?.files || e;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadSmart(files, setProgress);
      setPendingFiles((p) => [...p, ...uploaded]);
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const send = async () => {
    if (!text.trim() && pendingFiles.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { text, file_ids: pendingFiles.map((f) => f.id) });
      setMessages((m) => [...(m || []), data]);
      setText("");
      setPendingFiles([]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div {...dropProps(pickFiles, setDragOver)} className={`border bg-white transition-colors duration-200 ${dragOver ? "border-dashed border-slate-900" : "border-slate-200"}`} data-testid="project-messages">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <MessageSquare size={15} className="text-slate-500" />
        <span className="text-xs uppercase tracking-wider text-slate-500">{title}</span>
        {dragOver && <span className="text-xs text-slate-900 font-semibold ml-auto">Dosyaları buraya bırakın</span>}
      </div>
      <div ref={listRef} className="max-h-80 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
        {!messages ? (
          <p className="text-sm text-slate-500">Yükleniyor...</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="no-messages">{emptyText}</p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === myRole;
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                {!mine && (
                  <UserAvatar userId={m.sender_id} avatarId={m.sender_id ? "1" : null} name={m.sender_role === "admin" ? "GettProposal Ekibi" : m.sender_name} size={26} className="mb-0.5" />
                )}
                <div className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-line ${mine ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-900"}`}>
                  <div className={`text-[10px] uppercase tracking-wider mb-1 ${mine ? "text-slate-400" : "text-slate-500"}`}>
                    {m.sender_role === "admin" ? "GettProposal Ekibi" : m.sender_name} · {fmt(m.created_at)}
                  </div>
                  {m.text}
                  {m.files?.length > 0 && (
                    <div className={m.text ? "mt-2" : ""} data-testid={`message-files-${m.id}`}>
                      <FileChips files={m.files} />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      {(pendingFiles.length > 0 || uploading) && (
        <div className="border-t border-slate-200 px-4 py-3 space-y-2" data-testid="pending-files">
          {pendingFiles.length > 0 && (
            <FileChips files={pendingFiles} onRemove={(id) => setPendingFiles((p) => p.filter((f) => f.id !== id))} />
          )}
          {uploading && <UploadProgress progress={progress} />}
        </div>
      )}
      <div className="border-t border-slate-200 p-4 flex items-end gap-2">
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={pickFiles} data-testid="message-file-input" />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || busy}
          data-testid="message-attach-button"
          title="Dosya ekle"
          className="rounded-none h-11 px-3 border-slate-200 hover:bg-slate-100 transition-colors duration-200"
        >
          <Paperclip size={15} />
        </Button>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          placeholder={placeholder}
          data-testid="message-input"
          className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm flex-1 resize-none"
        />
        <Button onClick={send} disabled={busy || uploading || (!text.trim() && pendingFiles.length === 0)} data-testid="message-send-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Send size={14} /> <span className="hidden sm:inline">Gönder</span>
        </Button>
      </div>
    </div>
  );
};
